import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Filter, Clock, User, AlertCircle, CheckCircle, Loader, Zap, Download } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { adminMonitoringApi, adminUsersApi } from '../../lib/adminApi';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { SlideOver } from '../../components/admin/SlideOver';

interface PromptLog {
  id: string;
  session_id: string;
  user_id: string;
  prompt_type: string;
  provider: string;
  model_name: string;
  temperature: number;
  final_prompt: string;
  prompt_metadata: any;
  ai_response: string | null;
  response_metadata: any;
  credits_used: number;
  success: boolean;
  error_message: string | null;
  created_at: string;
  users?: {
    email: string;
    first_name: string;
    company_name: string;
  };
}

interface UserOption {
  id: string;
  name: string;
  email: string;
}

const promptTypeLabels: Record<string, string> = {
  idea_generate: 'Idee',
  article_generate: 'Artikel',
  article_write: 'Artikel',
  article_regenerate: 'Artikel Regen.',
  titles_generate: 'Titels',
  intros_generate: "Intro's",
  images_generate: 'Afbeeldingen',
  images_prompt_enhance: 'Prompt Verbetering',
  blog_to_email: 'Blog → Email',
  profile_generator_business: 'Profiel - Bedrijf',
  profile_generator_image: 'Profiel - Afb.',
  chatbot_query: 'Chatbot',
  article_direction_hints: 'Invalshoeken',
};

export function AdminPromptLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<PromptLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<PromptLog | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSuccess, setFilterSuccess] = useState<string>('all');
  const [filterUser, setFilterUser] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const pageSize = 50;

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [page, filterType, filterSuccess, filterUser]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await adminUsersApi.all();
      const userOptions = (response.users || []).map((u: any) => ({
        id: u.id,
        name: u.company_name || u.first_name || u.email,
        email: u.email,
      }));
      setUsers(userOptions);
    } catch (error) {
      console.error('Error loading users:', error);
    }
    setLoadingUsers(false);
  };

  const loadLogs = async () => {
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No session found');
        setLoading(false);
        return;
      }

      const { data, count } = await adminMonitoringApi.promptLogs({
        page,
        pageSize,
        filterType,
        filterSuccess,
        filterUser: filterUser === 'all' ? undefined : filterUser,
      });
      setLogs(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error loading logs:', error);
      setLogs([]);
      setTotalCount(0);
    }

    setLoading(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getResponseTime = (log: PromptLog): number | null => {
    if (log.response_metadata?.total_response_time_ms) {
      return log.response_metadata.total_response_time_ms;
    }
    if (log.response_metadata?.response_time_ms) {
      return log.response_metadata.response_time_ms;
    }
    return null;
  };

  const getResponseTimeColor = (ms: number): string => {
    if (ms < 1000) return 'text-green-600 bg-green-50';
    if (ms < 3000) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const downloadLog = (log: PromptLog) => {
    const exportData = {
      id: log.id,
      created_at: log.created_at,
      prompt_type: log.prompt_type,
      provider: log.provider,
      model_name: log.model_name,
      temperature: log.temperature,
      credits_used: log.credits_used,
      success: log.success,
      user: {
        id: log.user_id,
        email: log.users?.email,
        name: log.users?.company_name || log.users?.first_name,
      },
      session_id: log.session_id,
      prompt_metadata: log.prompt_metadata,
      final_prompt: log.final_prompt,
      ai_response: log.ai_response,
      response_metadata: log.response_metadata,
      error_message: log.error_message,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date(log.created_at).toISOString().slice(0, 16).replace('T', '_').replace(':', '-');
    a.href = url;
    a.download = `prompt-log_${log.prompt_type}_${date}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && logs.length === 0) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Laden...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-7 h-7 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Prompt Logs</h1>
        </div>
        <p className="text-sm text-gray-600">
          Bekijk alle verzonden AI prompts en responses voor debugging en analyse.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Alle types</option>
              <option value="idea_generate">Idee Generatie</option>
              <option value="article_write">Artikel Schrijven</option>
              <option value="article_regenerate">Artikel Herschrijven</option>
              <option value="titles_generate">Titels</option>
              <option value="intros_generate">Intro's</option>
              <option value="images_generate">Afbeeldingen</option>
              <option value="images_prompt_enhance">Prompt Verbetering</option>
              <option value="blog_to_email">Blog naar Email</option>
              <option value="profile_generator_business">Profiel - Bedrijfsinfo</option>
              <option value="profile_generator_image">Profiel - Afbeeldingsstijl</option>
              <option value="chatbot_query">Chatbot</option>
              <option value="article_direction_hints">Invalshoeken</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filterSuccess}
              onChange={(e) => {
                setFilterSuccess(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Alle statussen</option>
              <option value="true">Succesvol</option>
              <option value="false">Fout</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Gebruiker
            </label>
            <select
              value={filterUser}
              onChange={(e) => {
                setFilterUser(e.target.value);
                setPage(1);
              }}
              disabled={loadingUsers}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            >
              <option value="all">Alle gebruikers</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Per pagina
            </label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900 font-medium">
              {pageSize} items
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Totaal
            </label>
            <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900 font-semibold">
              {totalCount} logs
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Gebruiker
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Response Time
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Credits
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Datum
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => {
                const responseTime = getResponseTime(log);
                return (
                  <tr
                    key={log.id}
                    onClick={() => setSelectedLog(log)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-2 whitespace-nowrap">
                      {log.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {promptTypeLabels[log.prompt_type] || log.prompt_type}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="text-sm text-gray-900">
                        {log.users?.company_name || log.users?.first_name || 'Onbekend'}
                      </div>
                      <div className="text-xs text-gray-500">{log.users?.email}</div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className={`text-xs px-2 py-1 rounded font-medium ${
                        log.provider === 'leonardo'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {log.provider === 'leonardo' ? 'Leonardo' : log.model_name}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {responseTime !== null ? (
                        <div className="flex items-center gap-1">
                          <Zap className="w-3 h-3" />
                          <span className={`text-xs px-2 py-0.5 rounded font-semibold ${getResponseTimeColor(responseTime)}`}>
                            {responseTime}ms
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{log.credits_used}</span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-xs text-gray-600">
                        {formatDate(log.created_at)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {logs.length === 0 && !loading && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Geen logs gevonden</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            Vorige
          </button>
          <span className="text-sm text-gray-600">
            Pagina {page} van {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            Volgende
          </button>
        </div>
      )}

      <SlideOver
        isOpen={selectedLog !== null}
        onClose={() => setSelectedLog(null)}
        title="Prompt Log Details"
        width="wide"
      >
        {selectedLog && (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                {selectedLog.success ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-600" />
                )}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {promptTypeLabels[selectedLog.prompt_type] || selectedLog.prompt_type}
                  </h3>
                  <p className="text-sm text-gray-500">{formatDate(selectedLog.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm px-3 py-1 rounded font-medium ${
                  selectedLog.provider === 'leonardo'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {selectedLog.provider === 'leonardo' ? 'Leonardo' : selectedLog.model_name}
                </span>
                <button
                  onClick={() => downloadLog(selectedLog)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                  title="Download volledige log als JSON"
                >
                  <Download className="w-4 h-4" />
                  Download log
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-600 mb-1">Gebruiker</div>
                <div className="text-sm font-medium text-gray-900">
                  {selectedLog.users?.company_name || selectedLog.users?.first_name || 'Onbekend'}
                </div>
                <div className="text-xs text-gray-500">{selectedLog.users?.email}</div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-600 mb-1">Credits</div>
                <div className="text-lg font-semibold text-gray-900">{selectedLog.credits_used}</div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-600 mb-1">Temperature</div>
                <div className="text-lg font-semibold text-gray-900">
                  {selectedLog.temperature !== null ? selectedLog.temperature : 'N/A'}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-600 mb-1">Response Time</div>
                <div className="text-lg font-semibold text-gray-900">
                  {getResponseTime(selectedLog) !== null
                    ? `${getResponseTime(selectedLog)}ms`
                    : 'N/A'}
                </div>
              </div>

              {selectedLog.response_metadata?.total_tokens && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">Tokens</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {selectedLog.response_metadata.total_tokens}
                  </div>
                </div>
              )}

              {selectedLog.response_metadata?.num_images_generated && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">Afbeeldingen</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {selectedLog.response_metadata.num_images_generated}
                  </div>
                </div>
              )}

              {selectedLog.prompt_metadata?.user_profile_length && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">Profiel lengte</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {selectedLog.prompt_metadata.user_profile_length}
                  </div>
                </div>
              )}

              {selectedLog.response_metadata?.polling_attempts && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">Polling</div>
                  <div className="text-sm font-medium text-gray-900">
                    {selectedLog.response_metadata.polling_attempts} pogingen
                  </div>
                  <div className="text-xs text-gray-500">
                    {selectedLog.response_metadata.polling_duration_ms}ms
                  </div>
                </div>
              )}
            </div>

            {selectedLog.prompt_metadata && Object.keys(selectedLog.prompt_metadata).length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Prompt Metadata</h4>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <pre className="text-xs text-gray-700 font-mono overflow-x-auto">
                    {JSON.stringify(selectedLog.prompt_metadata, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Verzonden Prompt</h4>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                  {selectedLog.final_prompt}
                </pre>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {selectedLog.final_prompt.length} karakters
              </p>
            </div>

            {selectedLog.success && selectedLog.ai_response ? (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">AI Response</h4>
                {selectedLog.provider === 'leonardo' && selectedLog.response_metadata?.image_urls ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      {selectedLog.response_metadata.image_urls.map((url: string, idx: number) => (
                        <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                          <img
                            src={url}
                            alt={`Generated image ${idx + 1}`}
                            className="w-full h-auto"
                            loading="lazy"
                          />
                          <div className="p-2 bg-gray-50 text-xs text-gray-600">
                            Image {idx + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                    <details className="text-xs">
                      <summary className="cursor-pointer text-gray-600 hover:text-gray-900 font-medium">
                        Toon URLs
                      </summary>
                      <div className="mt-2 bg-white border border-gray-200 rounded p-2">
                        {selectedLog.response_metadata.image_urls.map((url: string, idx: number) => (
                          <div key={idx} className="mb-1">
                            <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                              {url}
                            </a>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-96 overflow-y-auto">
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                      {selectedLog.ai_response}
                    </pre>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {selectedLog.ai_response.length} karakters
                </p>
              </div>
            ) : selectedLog.error_message ? (
              <div>
                <h4 className="text-sm font-semibold text-red-900 mb-2">Error</h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">{selectedLog.error_message}</p>
                </div>
              </div>
            ) : null}

            {selectedLog.response_metadata && Object.keys(selectedLog.response_metadata).length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Response Metadata</h4>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <pre className="text-xs text-gray-700 font-mono overflow-x-auto">
                    {JSON.stringify(selectedLog.response_metadata, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                <div>
                  <span className="font-medium">User ID:</span>
                  <p className="font-mono mt-1 break-all">{selectedLog.user_id || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium">Session ID:</span>
                  <p className="font-mono mt-1 break-all">{selectedLog.session_id || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </SlideOver>
    </AdminLayout>
  );
}
